/**
 * The ship detail's controller and modal overlay (M6 plan P29), opened from a Fleet screen row through the
 * `fleetScreen` feature's `setShipOpener`.
 *
 * - **Controller** (`createShipDetailController`, Node-tested): reads the ship (`fleetShipView`), its captain log
 *   (`fleetFeed`) and its fit (the Career's hangar), builds the editor's choices, runs `reduceEditor` for every action
 *   and carries out its effects: a save goes out through the fleet service's `run` (`setOrder` or `applyPreset`), then
 *   the ship is re-read and the editor reset to what the worker now runs; close calls the owner's close.
 * - **Overlay** (`createShipDetailOverlay`): a layer above the Fleet screen holding `ui.modal`. Its window key
 *   listener takes every trusted key while open (Esc closes back to the Fleet screen; arrows, Enter and Space drive
 *   the editor); while a number field has the DOM focus, typing goes to it. The pad goes through `input`.
 *
 * UI events only.
 */
import type { UiState } from '../../app/feature'
import { enterModal } from '../../app/ui'
import { logger } from '../../core/logger'
import type { FleetService } from '../../fleet-link/service'
import type { StrategicClient } from '../../strategic/client'
import type { FleetLogEntry, FleetShipView } from '../../strategic/types'
import { mountLayerAfter, type NavInput, type ReactMount } from '../strategic/mount'
import type { FleetScreenConfig } from './fleetModel'
import { createEditorState, reduceEditor, type EditorAction, type EditorEffect, type EditorOptions, type EditorState, type OrderEditorData } from './orderModel'
import { ShipDetail, type LoadoutLine } from './ShipDetail'

export interface ShipDetailDeps {
  service: Pick<FleetService, 'run'>
  client: Pick<StrategicClient, 'fleetShipView' | 'fleetFeed'>
  data: OrderEditorData
  config: FleetScreenConfig
  /** The editor's choices for this ship (stations, systems, relays, escorts, wares). */
  options(ship: FleetShipView): Promise<EditorOptions> | EditorOptions
  /** The ship's fit from the Career's hangar, null when unknown. */
  loadout(shipId: string): readonly LoadoutLine[] | null
  /** Where the ship is, in words. */
  location(ship: FleetShipView): string
  onClose?: () => void
  /** A save went through (the Fleet screen re-reads its rows). */
  onSaved?: () => void
}

export interface ShipDetailController {
  readonly ship: FleetShipView | null
  readonly editor: EditorState | null
  readonly opts: EditorOptions | null
  readonly log: readonly FleetLogEntry[]
  readonly loadout: readonly LoadoutLine[] | null
  open(shipId: string): Promise<void>
  dispatch(a: EditorAction): void
  /** Resolves when the last save (and its re-read) finished; for tests. */
  settled(): Promise<void>
  onChange(fn: () => void): () => void
}

export function createShipDetailController(deps: ShipDetailDeps): ShipDetailController {
  let ship: FleetShipView | null = null
  let editor: EditorState | null = null
  let opts: EditorOptions | null = null
  let log: readonly FleetLogEntry[] = []
  let loadout: readonly LoadoutLine[] | null = null
  let pending: Promise<void> = Promise.resolve()
  let token = 0
  const listeners = new Set<() => void>()

  function changed(): void {
    for (const fn of [...listeners]) fn()
  }

  async function read(id: string, mine: number): Promise<FleetShipView | null> {
    const [s, l] = await Promise.all([deps.client.fleetShipView(id), deps.client.fleetFeed(deps.data.config.logLimit, { shipId: id })])
    if (mine !== token) return null
    ship = s
    log = l
    loadout = deps.loadout(id)
    return s
  }

  async function save(e: Extract<EditorEffect, { kind: 'save' }>): Promise<void> {
    const id = ship?.id
    const out = await deps.service.run(e.cmd)
    step({ type: 'saved', ok: out.ok, error: out.ok ? undefined : out.error })
    if (!out.ok || id === undefined) return
    deps.onSaved?.()
    const s = await read(id, token)
    if (s !== null) step({ type: 'reset', order: s.order, preset: s.preset, notice: 'Order saved' })
  }

  function step(a: EditorAction): void {
    if (editor === null || opts === null) return
    const r = reduceEditor(editor, a, { data: deps.data, opts })
    editor = r.state
    changed()
    if (r.effect === null) return
    if (r.effect.kind === 'close') {
      deps.onClose?.()
      return
    }
    const eff = r.effect
    pending = save(eff).catch((err: unknown) => {
      logger.warn('Ship detail: save failed', err)
      step({ type: 'saved', ok: false, error: 'failed' })
    })
  }

  return {
    get ship(): FleetShipView | null {
      return ship
    },
    get editor(): EditorState | null {
      return editor
    },
    get opts(): EditorOptions | null {
      return opts
    },
    get log(): readonly FleetLogEntry[] {
      return log
    },
    get loadout(): readonly LoadoutLine[] | null {
      return loadout
    },
    async open(shipId: string): Promise<void> {
      const mine = ++token
      ship = null
      editor = null
      opts = null
      log = []
      loadout = null
      changed()
      try {
        const s = await read(shipId, mine)
        if (s === null || mine !== token) return
        const o = await deps.options(s)
        if (mine !== token) return
        opts = o
        editor = createEditorState(s.order, s.preset)
        changed()
      } catch (err: unknown) {
        logger.warn('Ship detail: could not read the ship', err)
      }
    },
    dispatch: step,
    settled: () => pending,
    onChange(fn: () => void): () => void {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
  }
}

// ---------------------------------------------------------------------------------------------
// The modal overlay
// ---------------------------------------------------------------------------------------------

export interface ShipDetailOverlayOptions extends Omit<ShipDetailDeps, 'onClose'> {
  root: HTMLElement
  ui: UiState
  paused?: () => boolean
  /** Called after the overlay closed (the Fleet screen takes the keys back). */
  onClosed?: () => void
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>
}

export interface ShipDetailOverlay {
  readonly open: boolean
  show(shipId: string): void
  close(relock?: boolean): void
  input(i: NavInput): void
  dispose(): void
}

const KEY_NAV: Readonly<Record<string, NavInput>> = Object.freeze({
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  Space: 'activate',
  Enter: 'activate',
  NumpadEnter: 'activate',
})

export function createShipDetailOverlay(opts: ShipDetailOverlayOptions): ShipDetailOverlay {
  let mount: ReactMount | null = null
  let leave: ((relock?: boolean) => void) | null = null
  let off: (() => void) | null = null
  const target = opts.target ?? (typeof window === 'undefined' ? null : window)
  const controller = createShipDetailController({ ...opts, onClose: () => overlay.close() })

  function render(): void {
    const s = controller.ship
    mount?.render(
      <ShipDetail
        ship={s}
        loadout={controller.loadout}
        log={controller.log}
        location={s === null ? '' : opts.location(s)}
        config={opts.config}
        data={opts.data}
        opts={controller.opts}
        editor={controller.editor}
        dispatch={controller.dispatch}
      />,
    )
  }

  function onKey(e: KeyboardEvent): void {
    if (mount === null || !e.isTrusted) return
    e.stopImmediatePropagation()
    if (e.code === 'Escape') {
      e.preventDefault()
      if (!e.repeat) overlay.close()
      return
    }
    const typing = e.target instanceof HTMLInputElement
    if (typing && e.code !== 'ArrowUp' && e.code !== 'ArrowDown' && e.code !== 'Enter' && e.code !== 'NumpadEnter') return
    if (typing) e.target.blur()
    if (e.code === 'Tab') {
      e.preventDefault()
      controller.dispatch({ type: 'nav', input: e.shiftKey ? 'up' : 'down' })
      return
    }
    const i = KEY_NAV[e.code]
    if (i === undefined) return
    e.preventDefault()
    controller.dispatch({ type: 'nav', input: i })
  }
  target?.addEventListener('keydown', onKey, { capture: true })

  const overlay: ShipDetailOverlay = {
    get open(): boolean {
      return mount !== null
    },
    show(shipId: string): void {
      if (mount === null) {
        mount = mountLayerAfter(opts.root, 'sui-layer sd-layer')
        leave = enterModal(opts.ui, () => {
          overlay.close()
          return true
        })
        off = controller.onChange(render)
      }
      render()
      void controller.open(shipId)
    },
    close(relock = true): void {
      if (mount === null) return
      off?.()
      off = null
      mount.unmount()
      mount = null
      const l = leave
      leave = null
      l?.(relock && opts.paused?.() !== true)
      opts.onClosed?.()
    },
    input(i: NavInput): void {
      if (mount === null) return
      controller.dispatch({ type: 'nav', input: i })
    },
    dispose(): void {
      overlay.close(false)
      target?.removeEventListener('keydown', onKey, { capture: true })
    },
  }
  return overlay
}
